/* eslint-disable */

// Add these helper functions outside your component function, e.g., after the imports.

/**
 * Gets the translated header text for a given permission key.
 * @param {function} t - The i18next translation function.
 * @param {string} permissionKey - The permission key from the backend (e.g., "can_view_all_data").
 * @returns {string} The translated header text.
 */
export const getPermissionHeader = (t, permissionKey) => {
  switch (permissionKey) {
    case "can_view_all_data":
      return t("program_setup.table_head.can_view_all_data", "View All Data");
    case "can_manage_program_setup":
      return t(
        "program_setup.table_head.can_manage_program_setup",
        "Manage Program Setup"
      );
    case "can_manage_users_and_roles":
      return t(
        "program_setup.table_head.can_manage_users_and_roles",
        "Manage Users & Roles"
      );
    case "can_manage_financial_settings":
      return t(
        "program_setup.table_head.can_manage_financial_settings",
        "Manage Financial Settings"
      );
    case "can_view_initiatives_list":
      return t(
        "program_setup.table_head.can_view_initiatives_list",
        "View Initiatives List"
      );
    case "can_view_initiative_details":
      return t(
        "program_setup.table_head.can_view_initiative_details",
        "View Initiative Details"
      );
    case "can_access_control_panel":
      return t(
        "program_setup.table_head.can_access_control_panel",
        "Access Control Panel"
      );
    case "can_access_risk_module":
      return t(
        "program_setup.table_head.can_access_risk_module",
        "Access Risk Module"
      );
    case "can_access_meeting_management":
      return t(
        "program_setup.table_head.can_access_meeting_management",
        "Access Meeting Management"
      );
    case "can_create_initiatives":
      return t(
        "program_setup.table_head.can_create_initiatives",
        "Create Initiatives"
      );
    case "can_edit_initiatives":
      return t(
        "program_setup.table_head.can_edit_initiatives",
        "Edit Initiatives"
      );
    case "can_change_initiative_stage":
      return t(
        "program_setup.table_head.can_change_initiative_stage",
        "Change Initiative Stage"
      );
    case "can_report_one_page":
      return t(
        "program_setup.table_head.can_report_one_page",
        "Report One-Page"
      );
    case "can_approve_one_page":
      return t(
        "program_setup.table_head.can_approve_one_page",
        "Approve One-Page"
      );
    case "can_report_kpi_values":
      return t(
        "program_setup.table_head.can_report_kpi_values",
        "Report KPI Values"
      );
    case "can_approve_kpi_values":
      return t(
        "program_setup.table_head.can_approve_kpi_values",
        "Approve KPI Values"
      );
    case "can_edit_risks":
      return t("program_setup.table_head.can_edit_risks", "Edit Program Risks");
    case "can_access_portfolio":
      return t(
        "program_setup.table_head.can_access_portfolio",
        "Access Portfolio"
      );
    case "can_access_visibility_and_rhythm":
      return t(
        "program_setup.table_head.can_access_visibility_and_rhythm",
        "Access Visibility & Rhythm"
      );
    case "can_edit_deliverables":
      return t(
        "program_setup.table_head.can_edit_deliverables",
        "Edit Deliverables"
      );
    case "can_approve_deliverables":
      return t(
        "program_setup.table_head.can_approve_deliverables",
        "Approve Deliverables"
      );
    default:
      // Fallback for any new, unmapped permissions
      return permissionKey;
  }
};

/**
 * Gets the translated tooltip text for a given permission key.
 * @param {function} t - The i18next translation function.
 * @param {string} permissionKey - The permission key from the backend.
 * @returns {string} The translated tooltip text.
 */
export const getPermissionTooltip = (t, permissionKey) => {
  switch (permissionKey) {
    case "can_view_all_data":
      return t(
        "permissions.help.can_view_all_data",
        "Bypasses access group restrictions. For C-Level/Admins."
      );
    case "can_manage_program_setup":
      return t(
        "permissions.help.can_manage_program_setup",
        "Can access 'Program Setup' screens (Branches, KPIs, etc.)."
      );
    case "can_manage_users_and_roles":
      return t(
        "permissions.help.can_manage_users_and_roles",
        "Can invite/manage users and edit roles/access groups."
      );
    case "can_manage_financial_settings":
      return t(
        "permissions.help.can_manage_financial_settings",
        "Can view and edit Exchange Rates."
      );
    case "can_view_initiatives_list":
      return t(
        "permissions.help.can_view_initiatives_list",
        "Can view the main initiative list/board."
      );
    case "can_view_initiative_details":
      return t(
        "permissions.help.can_view_initiative_details",
        "Can view the detail page of initiatives within scope."
      );
    case "can_access_control_panel":
      return t(
        "permissions.help.can_access_control_panel",
        "Can view and interact with the Control Panel module."
      );
    case "can_access_risk_module":
      return t(
        "permissions.help.can_access_risk_module",
        "Can view the Program Risks module."
      );
    case "can_access_meeting_management":
      return t(
        "permissions.help.can_access_meeting_management",
        "Can access the Meeting Management module."
      );
    case "can_create_initiatives":
      return t("permissions.help.can_create_initiatives", "");
    case "can_edit_initiatives":
      return t(
        "permissions.help.can_edit_initiatives",
        "Can edit initiatives within their scope (SaveInitiativeView)."
      );
    case "can_change_initiative_stage":
      return t("permissions.help.can_change_initiative_stage", "");
    case "can_report_one_page":
      return t("permissions.help.can_report_one_page", "");
    case "can_approve_one_page":
      return t("permissions.help.can_approve_one_page", "");
    case "can_report_kpi_values":
      return t(
        "permissions.help.can_report_kpi_values",
        "Can input actual values for KPIs (InitiativeResults/CentralUpload)."
      );
    case "can_approve_kpi_values":
      return t("permissions.help.can_approve_kpi_values", "");
    case "can_edit_risks":
      return t(
        "permissions.help.can_edit_risks",
        "Can create, edit, and delete Program Risks."
      );
    case "can_access_portfolio":
      return t(
        "permissions.help.can_access_portfolio",
        "Can access the Portfolio module and initiative prioritization."
      );
    case "can_access_visibility_and_rhythm":
      return t(
        "permissions.help.can_access_visibility_and_rhythm",
        "Can access the program Visibility and Rhythm module."
      );
    case "can_edit_deliverables":
      return t(
        "permissions.help.can_edit_deliverables",
        "Can edit initiative deliverables."
      );
    case "can_approve_deliverables":
      return t(
        "permissions.help.can_approve_deliverables",
        "Can approve initiative deliverables."
      );
    default:
      return ""; // Return empty string for tooltips if no mapping exists
  }
};
